import { useEffect, useLayoutEffect, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { addLoadingPage } from "@/utils/route-transitions";

interface Props {
  className?: string;
}

// The spinners on screen (when each mounted) and when the last one anyone
// saw went away. Pages whose code loads in two steps hand from one spinner
// to the next, and the next shows at once instead of vanishing for its delay.
const mountedSpinners = new Set<{ mountedAt: number }>();
let lastSeenSpinnerGoneAt = 0;
const DELAY = 250;

function spinnerAlreadyShowing(now: number) {
  if (now - lastSeenSpinnerGoneAt < 300) {
    return true;
  }
  for (const spinner of mountedSpinners) {
    if (now - spinner.mountedAt >= DELAY) {
      return true;
    }
  }
  return false;
}

// Shown while a page's code loads, until the page itself (with its own
// skeleton) fades in. It waits a moment before appearing, so a quick load
// shows no spinner at all.
export function LoadingPage({ className }: Props) {
  const [showAtOnce] = useState(() => spinnerAlreadyShowing(Date.now()));

  useLayoutEffect(() => addLoadingPage(), []);

  useEffect(() => {
    const spinner = { mountedAt: Date.now() - (showAtOnce ? DELAY : 0) };
    mountedSpinners.add(spinner);
    return () => {
      mountedSpinners.delete(spinner);
      if (Date.now() - spinner.mountedAt >= DELAY) {
        lastSeenSpinnerGoneAt = Date.now();
      }
    };
  }, [showAtOnce]);

  return (
    <div
      aria-busy
      className={cn(
        !showAtOnce && "loading-page",
        "flex-center absolute inset-0",
        className,
      )}
    >
      <Spinner size={32} />
    </div>
  );
}
